/**
 * 카카오(다음) 우편번호 서비스 로더.
 *
 * <p>키 발급이나 과금이 없고 스크립트 한 줄로 동작한다. 주소 입력이 있는 화면(주문서, 배송지 관리)
 * 에서만 필요하므로 index.html 에 넣지 않고 처음 쓰는 시점에 한 번만 로드한다.
 * 문서: https://postcode.map.daum.net/guide
 */
const SCRIPT_SRC = 'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';

let loading: Promise<void> | null = null;

export function loadDaumPostcode(): Promise<void> {
  if (window.daum?.Postcode) return Promise.resolve();
  if (!loading) {
    loading = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        loading = null;
        script.remove();
        reject(new Error('우편번호 서비스를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.'));
      };
      document.head.appendChild(script);
    });
  }
  return loading;
}

export interface PostcodeResult {
  zipcode: string;
  address1: string;
}

/**
 * 우편번호·주소가 모두 채워졌는지. 읽기 전용 입력은 브라우저가 `required` 검사를
 * 건너뛰므로 폼 제출 전에 직접 호출한다.
 */
export function isAddressFilled(form: PostcodeResult): boolean {
  return form.zipcode.trim() !== '' && form.address1.trim() !== '';
}

/**
 * 우편번호 검색창을 띄우고 사용자가 고른 주소를 돌려준다.
 * 창을 그냥 닫으면 null.
 *
 * <p>검색창은 별도 팝업이라 본 화면을 클릭해 뒤로 보내면 닫히지 않은 채 남고, 이때는
 * 결과가 오지 않는다. 그래서 "여는 중" 같은 상태는 결과가 아니라 `onOpen` 으로 풀어야 한다.
 * 다시 호출하면 같은 popupKey 의 기존 팝업을 재사용한다.
 */
export async function searchPostcode(onOpen?: () => void): Promise<PostcodeResult | null> {
  await loadDaumPostcode();
  return new Promise((resolve) => {
    let picked: PostcodeResult | null = null;
    new window.daum!.Postcode({
      oncomplete: (data) => {
        picked = { zipcode: data.zonecode, address1: formatAddress(data) };
      },
      // oncomplete 뒤에도 onclose 가 호출되므로 여기서 한 번만 resolve 한다.
      onclose: () => resolve(picked),
    }).open({ popupKey: 'gency03-postcode' });
    onOpen?.();
  });
}

/** 도로명/지번 중 사용자가 고른 쪽을 쓰고, 도로명이면 동·건물명을 괄호로 덧붙인다. */
function formatAddress(data: DaumPostcodeData): string {
  if (data.userSelectedType === 'J') return data.jibunAddress;

  const extras: string[] = [];
  if (data.bname && /[동로가]$/.test(data.bname)) extras.push(data.bname);
  if (data.buildingName && data.apartment === 'Y') extras.push(data.buildingName);
  return extras.length ? `${data.roadAddress} (${extras.join(', ')})` : data.roadAddress;
}
